import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';

import gettext from 'js-utils/gettext';
import { IMAGE_TYPE_EVENT_LOGO } from 'js-utils/http';
import { connectImageField } from 'js-utils/redux';

import Tabs from 'eventbrite_design_system/tabs/Tabs';
import Layout from 'eventbrite_design_system/layout/Layout';
import CalendarGraphicSvg from 'eventbrite_design_system/iconography/icons/CalendarGraphic';
import Divider from 'eventbrite_design_system/divider/Divider';
import SvgAlertChunky from 'eventbrite_design_system/iconography/icons/AlertChunky';
import _ImageField from 'eventbrite_design_system/imageField/ImageField';
import { TYPE_ERROR } from 'eventbrite_design_system/notification/constants';
import { CROP_REQUIRED } from 'eventbrite_design_system/imageField/constants';
import {
    withEventSaveNotification,
    collections as collectionsDuck,
    constants as destinationConstants,
} from '@eb/destination-utils';
import { GA_SETTINGS_PROP_TYPE } from '@eb/site-analytics';

import PageHeaderEdit from '../components/PageHeaderEdit';
import CollectionSummary from '../components/CollectionSummary';
import EventsSection from '../components/EventsSection';
import PageHeader from '../components/PageHeader';
import ConditionalComponent from '../components/ConditionalComponent';
import OwnerActionBar from '../components/OwnerActionBar';
import FollowCTA from '../components/FollowCTA';
import { ADD_IMAGE_GA_ACTION, SHARE_EVENT_GA_ACTION } from '../constants';

import './collectionPage.scss';

const {
    constants: {
        COLLECTION_PROP_TYPE,
        CREATE_COLLECTION_VIEW_GA_ACTION,
        SHOW_ADD_TO_COLLECTION_GA_ACTION,
    },
} = collectionsDuck;
const CROP_2_TO_1 = 2;
const ImageField = connectImageField(_ImageField, 'collection', 'imageLarge');

const DivPageWrapper = ({ children }) => <div>{children}</div>;

const FormPageWrapper = ({ children, _handleSubmit }) => (
    <form onSubmit={_handleSubmit} data-spec="event-section-form">
        {children}
    </form>
);

const HeaderBackground = ({
    collectionImageUrl,
    collectionName,
    collectionImageArtistAttribution,
    isEditMode,
}) => {
    if (isEditMode) {
        return (
            <div
                className="
                eds-l-sn-mar-top-8
                eds-l-sm-mar-top-8
                eds-l-sw-mar-top-8
                eds-l-mn-mar-left-12
                eds-l-md-mar-left-12
                eds-l-mw-mar-left-12
                eds-l-ln-mar-left-12
                eds-l-lg-mar-left-12
                eds-l-lw-mar-left-12
            "
            >
                <div className="user-collection__header-image eds-align--center">
                    <ImageField
                        imageType={IMAGE_TYPE_EVENT_LOGO}
                        imageAlt="Collection image"
                        hiddenLabel="Collection image"
                        cropIntention={CROP_REQUIRED}
                        cropAspectRatio={CROP_2_TO_1}
                        onReceiveFile={this._handleImageClick}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="user-collection__header-image-container-extra">
            <div
                className="user-collection__header-image eds-show-up-mn"
                style={{
                    backgroundImage: `url(${collectionImageUrl})`,
                }}
            />
            <img
                className="user-collection__header-image eds-show-down-sw"
                src={collectionImageUrl}
                alt={collectionName}
            />
            {collectionImageArtistAttribution && (
                <p
                    className="
            user-collection__header-image-artist-attribution
            eds-text-bs
            eds-text-color--white
        "
                >
                    {collectionImageArtistAttribution}
                </p>
            )}
        </div>
    );
};

class CollectionPage extends PureComponent {
    static propTypes = {
        isLoading: PropTypes.bool,
        events: PropTypes.arrayOf(PropTypes.object),
        isEventsLoading: PropTypes.bool,
        isOwner: PropTypes.bool,
        isQuality: PropTypes.bool,
        isFollowing: PropTypes.bool,
        userId: PropTypes.string,
        collectionName: PropTypes.string,
        collectionSummary: PropTypes.string,
        creatorName: PropTypes.string,
        creatorImage: PropTypes.string,
        shareUrl: PropTypes.string,
        handleCollectionEdit: PropTypes.func,
        onSaveEvent: PropTypes.func,
        handleViewEvent: PropTypes.func,
        hasUpcomingPagination: PropTypes.bool,
        handleSeeMoreEvents: PropTypes.func,
        userCollections: COLLECTION_PROP_TYPE,
        onAddOrRemoveEventToCollection: PropTypes.func,
        onCreateCollection: PropTypes.func,
        handleFollowCollection: PropTypes.func,
        handleUnfollowCollection: PropTypes.func,
        isPublic: PropTypes.bool,
        displayCreatorName: PropTypes.string,
        displayCreatorDescription: PropTypes.string,
        /*
         * `true` if the user is authenticated
         */
        isAuthenticated: PropTypes.bool,
        /*
         * `true` if the user is editing the collection
         */
        isEditMode: PropTypes.bool.isRequired,
        /*
         * `true` if feature flag is enabled
         */
        showFeaturedOrganizers: PropTypes.bool,
        /*
         * Settings for Google Analytics
         */
        gaSettings: GA_SETTINGS_PROP_TYPE,
        /*
         * Category for Google Analytics, used by follow organizer
         */
        gaCategory: PropTypes.string,
        /*
         * List of organizer IDs for the featured organizers
         */
        organizerIds: PropTypes.arrayOf(PropTypes.string),
    };

    componentDidUpdate(prevProps) {
        const { hasError, addMainNotification, clearError } = this.props;
        const { hasError: prevHasError } = prevProps;

        if (!prevHasError && hasError) {
            addMainNotification({
                children: gettext(
                    'We encountered a server error. Please try again.',
                ),
                onClose: clearError,
                hasCloseButton: true,
                isNotificationSticky: true,
                iconType: <SvgAlertChunky />,
                type: TYPE_ERROR,
            });
        }
    }

    _handleSeeMore = (filter) => {
        const { hasUpcomingPagination, handleSeeMoreEvents } = this.props;

        if (hasUpcomingPagination) {
            handleSeeMoreEvents(filter);
        }
    };

    _handleImageClick = () => {
        const { collectionId, trackEvent } = this.props;

        trackEvent(ADD_IMAGE_GA_ACTION, collectionId);
    };

    _handleShowCreateCollectionView = () => {
        const { trackEvent, userCollections } = this.props;

        trackEvent(CREATE_COLLECTION_VIEW_GA_ACTION, userCollections.length);
    };

    _handleShowAddToCollectionView = () => {
        const { trackEvent, userCollections } = this.props;

        trackEvent(SHOW_ADD_TO_COLLECTION_GA_ACTION, userCollections.length);
    };

    _handleDidShareEvent = (eid) => {
        const { trackEvent } = this.props;

        trackEvent(SHARE_EVENT_GA_ACTION, eid);
    };

    _handleViewEvent = (eventId) => {
        const { trackEvent } = this.props;

        trackEvent(destinationConstants.VIEW_EVENT_GA_ACTION, eventId);
    };

    _getTabContent = () => {
        const {
            events,
            onSaveEvent,
            userCollections,
            handleClickBrowseEvents,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            isEditMode,
            isEventsLoading,
            hasUpcomingPagination,
            isAuthenticated,
            isQuality,
            showFeaturedOrganizers,
            organizerIds,
            gaSettings,
            gaCategory,
            userId,
        } = this.props;

        const sharedEventSectionProps = {
            userId,
            userCollections,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            isEditMode,
            isQuality,
            isAuthenticated,
            isLoading: isEventsLoading,
            onSaveEvent: onSaveEvent,
            handleOnView: this._handleViewEvent,
            handleOnDidShare: this._handleDidShareEvent,
            onShowCreateCollectionView: this._handleShowCreateCollectionView,
            onShowAddToCollectionView: this._handleShowAddToCollectionView,
        };

        return [
            {
                displayName: gettext('Upcoming'),
                value: 0,
                content: (
                    <EventsSection
                        {...sharedEventSectionProps}
                        events={events}
                        onSeeMore={this._handleSeeMore}
                        hasPagination={hasUpcomingPagination}
                        userHasNoEvents={isEmpty(events)}
                        emptyStateConfig={{
                            graphicType: <CalendarGraphicSvg />,
                            description: gettext('Your calendar is wide open!'),
                            title: gettext(
                                "Let's find your next favorite thing.",
                            ),
                        }}
                        handleClickBrowseEvents={handleClickBrowseEvents}
                        showFeaturedOrganizers={showFeaturedOrganizers}
                        organizerIds={organizerIds}
                        gaSettings={gaSettings}
                        gaCategory={gaCategory}
                    />
                ),
            },
        ];
    };

    render() {
        const {
            isOwner,
            collectionId,
            collectionName,
            collectionSummary,
            collectionImageUrl,
            collectionImageArtistAttribution,
            creatorName,
            creatorImage,
            location,
            shareUrl,
            getShareUrl,
            userId,
            toggleEditMode,
            isEditMode,
            saveCollectionEdits,
            handleSubmit,
            trackEvent,
            displayCreatorName,
            displayCreatorDescription,
            isQuality,
            isPublic,
            handleFollowCollection,
            handleUnfollowCollection,
            isFollowing,
            showFeaturedOrganizers,
            invalid,
            submitting,
            pristine,
            reset,
        } = this.props;
        const tabContent = this._getTabContent();

        let imageCmp;
        let PageHeaderClass = PageHeader;
        let PageWrapper = DivPageWrapper;

        if (collectionImageUrl) {
            imageCmp = (
                <HeaderBackground
                    collectionImageUrl={collectionImageUrl}
                    collectionName={collectionName}
                    collectionImageArtistAttribution={
                        collectionImageArtistAttribution
                    }
                    isEditMode={isEditMode}
                />
            );
        }

        if (isEditMode) {
            PageHeaderClass = PageHeaderEdit;
            PageWrapper = FormPageWrapper;
        }

        const ownerActionBarNode = (
            <ConditionalComponent shouldRender={isOwner}>
                <OwnerActionBar
                    collectionId={collectionId}
                    toggleEditMode={toggleEditMode}
                    shareUrl={shareUrl}
                    getShareUrl={getShareUrl}
                    userId={userId}
                    isEditMode={isEditMode}
                    saveCollectionEdits={saveCollectionEdits}
                    isSaveDisabled={pristine || submitting || invalid}
                    trackEvent={trackEvent}
                    onCancel={reset}
                    isEditorial={isQuality}
                />
            </ConditionalComponent>
        );

        // For blur effect on desktop
        const imageMask = (
            <div
                className="
                    user-collection__header-image-mask-container
                    eds-l-ln-mar-hor-6
                    eds-l-lg-mar-hor-6
                    eds-l-lw-mar-hor-6
                "
            >
                <img
                    className="user-collection__header-image-mask"
                    src={collectionImageUrl}
                    alt="blur-decoration"
                />
            </div>
        );

        const followCTANode = (
            <ConditionalComponent shouldRender={isQuality && !isEditMode}>
                <FollowCTA
                    handleFollowCollection={handleFollowCollection}
                    handleUnfollowCollection={handleUnfollowCollection}
                    isFollowing={isFollowing}
                    isPublic={isPublic}
                    trackEvent={trackEvent}
                    userId={userId}
                    location={location}
                    collectionId={collectionId}
                    collectionName={collectionName}
                    collectionImage={collectionImageUrl}
                />
            </ConditionalComponent>
        );

        const pageHeaderCmp = (
            <PageHeaderClass
                collectionName={collectionName}
                collectionSummary={collectionSummary}
                creatorName={creatorName}
                creatorImage={creatorImage}
                displayCreatorName={displayCreatorName}
                displayCreatorDescription={displayCreatorDescription}
                isQuality={isQuality}
                isEditMode={isEditMode}
                collectionImageUrl={collectionImageUrl}
                handleFollowCollection={handleFollowCollection}
                handleUnfollowCollection={handleUnfollowCollection}
                isFollowing={isFollowing}
                isPublic={isPublic}
                followCTANode={followCTANode}
                ownerActionBarNode={ownerActionBarNode}
                isOwner={isOwner}
            />
        );

        let headerOnlyDesktop;
        let headerAndImageDesktop;

        if (imageCmp) {
            headerAndImageDesktop = (
                <div className="user-collection__header-image-container">
                    {pageHeaderCmp}
                    {imageCmp}
                </div>
            );
        }

        if (!headerAndImageDesktop) {
            headerOnlyDesktop = (
                <div className="user-collection__header-only-container eds-l-mar-top-6">
                    {pageHeaderCmp}
                </div>
            );
        }

        const eventsContainerClassNames = classnames({
            'user-collection__events-section': !showFeaturedOrganizers,
            'user-collection__events-section--featured-organizers': showFeaturedOrganizers,
        });

        const eventsContainer = (
            <div className={eventsContainerClassNames}>
                {// Don't show tabs for editorial collection
                isQuality ? (
                    tabContent[0].content
                ) : (
                    <Tabs
                        items={tabContent}
                        onTabChange={this._handleTabChange}
                    />
                )}
            </div>
        );

        // Use of double layout is to allow the image to go past the layout max-width
        const desktopLayout = (
            <main className="eds-show-up-mn eds-l-ln-mar-top-12 eds-l-lg-mar-top-12 eds-l-lw-mar-top-12">
                <section>
                    {!isEditMode && collectionImageUrl && imageMask}
                    <div>
                        {headerAndImageDesktop}
                        {headerOnlyDesktop && (
                            <Layout
                                maxWidth="large"
                                hasHorizontalGutters={true}
                            >
                                {headerOnlyDesktop}
                            </Layout>
                        )}
                        <div
                            className={classnames(
                                { 'eds-l-mar-top-8': collectionImageUrl },
                                { 'eds-l-mar-top-16': !collectionImageUrl },
                            )}
                        >
                            <Layout
                                maxWidth="large"
                                hasHorizontalGutters={true}
                            >
                                {eventsContainer}
                            </Layout>
                        </div>
                    </div>
                </section>
            </main>
        );

        const mobileLayout = (
            <main className="eds-show-down-sw">
                {imageCmp}
                <Layout maxWidth="large" hasHorizontalGutters={true}>
                    {pageHeaderCmp}
                    <div className="eds-show-down-sw">
                        {isOwner && !isEditMode
                            ? ownerActionBarNode
                            : followCTANode}
                        <ConditionalComponent shouldRender={collectionSummary}>
                            <div className="eds-l-mar-top-2 eds-l-mar-bot-8">
                                <CollectionSummary
                                    collectionSummary={collectionSummary}
                                    isEditMode={isEditMode}
                                />
                            </div>
                        </ConditionalComponent>
                        <Divider color="grey-200" />
                        {eventsContainer}
                    </div>
                </Layout>
            </main>
        );

        return (
            <PageWrapper _handleSubmit={handleSubmit}>
                {desktopLayout}
                {mobileLayout}
            </PageWrapper>
        );
    }
}

export default withEventSaveNotification(CollectionPage);
